import type { ComponentProps, ComponentType } from 'react'
// eslint-disable-next-line no-restricted-imports
import React, { lazy as reactLazy } from 'react'
import { announce, collect } from 'kidsout:module-usage'

/**
 * `lazy`, but the module it loads is announced on every render rather than once.
 *
 * React calls a lazy factory once per process, and the import inside it announces itself as it
 * runs — so on the server only the first render that mounted the component reported the module,
 * and every later request rendered its markup with none of its stylesheets. The ids the factory
 * announced are kept and repeated here instead.
 */
export const lazy = <T extends ComponentType<any>>(factory: () => Promise<{ default: T }>): T => {
  let ids: Set<string> = new Set()

  const Component = reactLazy(() => {
    const collected = collect(factory)
    ids = collected.ids
    return collected.result
  })

  const Lazy = (props: ComponentProps<T>) => {
    for (const id of ids) announce(id)
    return <Component {...props} />
  }
  Lazy.displayName = 'Lazy'

  return Lazy as unknown as T
}
